/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from "react";
import "./css/StaffPage.css"; // reuse same CSS
import {
  generateSalary,
  bulkGenerateSalary,
  getAllSalaries,
  getSalaryById,
  getStaffSalaryHistory,
  updateSalary,
  approveSalary,
  bulkApproveSalary,
  markSalaryPaid,
  bulkMarkPaid,
  toggleHold,
  deleteSalary,
  downloadSalarySlip,
  emailSalarySlip,
  bulkEmailSlips,
} from "../api/salaryApi";
import { getAllStaff } from "../api/staffApi";
import { getMonthlyReport } from "../api/attendanceApi";
import {
  FiSearch,
  FiX,
  FiUsers,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
  FiFileText,
  FiCheck,
  FiCreditCard,
  FiPauseCircle,
  FiPlayCircle,
  FiTrash2,
  FiPlus,
  FiEdit2,
  FiSave,
  FiEye,
  FiClock,
  FiTrendingUp,
  FiDownload,
  FiMail,
  FiSend,
} from "react-icons/fi";
import AccessControl from "../components/AccessControl";
import { BsCurrencyRupee } from "react-icons/bs";

// ── Constants ─────────────────────────────────────────────────────────────
const ADMIN_PANEL_ROLES = ["SUPERADMIN", "ADMIN"];
const READ_ROLES        = ["SUPERADMIN", "ADMIN", "HR", "ACCOUNTS"];
const LIMIT = 20;

const STATUS_META = {
  DRAFT:    { label: "Draft",    color: "#444441", bg: "#F1EFE8", dot: "#888780" },
  APPROVED: { label: "Approved", color: "#0C447C", bg: "#E6F1FB", dot: "#378ADD" },
  PAID:     { label: "Paid",     color: "#085041", bg: "#E1F5EE", dot: "#1d9e75" },
  ON_HOLD:  { label: "On Hold",  color: "#A32D2D", bg: "#FCEBEB", dot: "#e53e3e" },
};

const STATUSES = Object.keys(STATUS_META);

const PAYMENT_MODES = ["BANK_TRANSFER", "CASH", "CHEQUE"];

const currentMonthIST = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }).slice(0, 7);

// Salary for month M uses attendance/LOP from the PREVIOUS month (M - 1)
const prevMonthOf = (m) => {
  if (!m) return m;
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(y, mo - 2, 1); // mo is 1-12; -2 lands on prev month, 0-indexed
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const formatCurrency = (val) =>
  `₹${Number(val || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

// "2025-05" → "May 2025"
const formatMonth = (m) => {
  if (!m) return m;
  const [y, mo] = m.split("-").map(Number);
  return new Date(y, mo - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
};

// Display month = attendanceMonth (actual salary period). Fallback to month for old records.
const displayMonth = (rec) => rec?.attendanceMonth || rec?.month || "";

export default function SalaryPage() {
  const adminData = localStorage.getItem("admin");
  const userData  = adminData ? JSON.parse(adminData) : null;
  const myRole    = userData?.role || "";
  const canManage = ADMIN_PANEL_ROLES.includes(myRole);

  // ── Common state ──────────────────────────────────────────────────────
  const [loading, setLoading]       = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast]           = useState({ show: false, message: "", type: "" });

  // ── List state ────────────────────────────────────────────────────────
  const [month, setMonth]         = useState(currentMonthIST());
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm]     = useState("");
  const [salaries, setSalaries]   = useState([]);
  const [totals, setTotals]       = useState(null);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0 });
  const [page, setPage]           = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);

  const debounceRef = useRef(null);

  // ── Generate modal state ──────────────────────────────────────────────
  const [generateMode, setGenerateMode] = useState(false);
  const [genStep, setGenStep]           = useState("select"); // select | configure
  const [genMonth, setGenMonth]         = useState(currentMonthIST()); // month to generate salary for — independently selectable
  const [attMonth, setAttMonth]         = useState(prevMonthOf(currentMonthIST())); // attendance month — defaults to genMonth-1, but user can override
  const [lopMap, setLopMap]             = useState({}); // staffId -> LOP days, sourced from Attendance page's monthly report
  const [lopLoading, setLopLoading]     = useState(false);
  const [paidStaffIds, setPaidStaffIds] = useState(new Set()); // staffIds already PAID for genMonth — locked, can't regenerate
  const [staffSearch, setStaffSearch]   = useState("");
  const [staffResults, setStaffResults] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState([]); // [{ _id, name, role, department, gross }]

  // ── Detail / Edit modal ───────────────────────────────────────────────
  const [detailSalary, setDetailSalary] = useState(null);
  const [editMode, setEditMode]   = useState(false);
  const [editForm, setEditForm]   = useState({ bonus: 0, bonusNote: "", remarks: "", tds: 0, otherDeductions: 0, otherAllowances: 0, professionalTax: 0 });

  // ── Pay modal ─────────────────────────────────────────────────────────
  const [payTarget, setPayTarget] = useState(null); // { id } or { ids: [...] }
  const [payForm, setPayForm]     = useState({ paymentMode: "BANK_TRANSFER", transactionRef: "" });

  // ── History modal ─────────────────────────────────────────────────────
  const [historyStaff, setHistoryStaff] = useState(null);
  const [historyData, setHistoryData]   = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ── Confirm delete ─────────────────────────────────────────────────────
  const [confirmDelete, setConfirmDelete] = useState(null);

  // ── Slip download/email state ───────────────────────────────────────
  const [downloadingId, setDownloadingId] = useState(null);
  const [emailModal, setEmailModal]       = useState(null); // salary obj or { bulk: true }
  const [emailForm, setEmailForm]         = useState({ toEmail: "" });
  const [emailing, setEmailing]           = useState(false);

  // ── Toast helper ──────────────────────────────────────────────────────
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  // ── Fetch salary list ─────────────────────────────────────────────────
  const fetchSalaries = useCallback(async (m, p, status, search) => {
    setLoading(true);
    try {
      const res = await getAllSalaries({
        month: m, n_page: p, n_limit: LIMIT,
        status: status === "all" ? "" : status,
        c_search_term: search,
      });
      if (res.success) {
        setSalaries(res.salaries || []);
        setTotals(res.totals || null);
        setPagination(res.pagination || { currentPage: 1, totalPages: 1, totalCount: 0 });
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchSalaries(month, page, statusFilter, searchTerm);
    setSelectedIds([]);
  }, [month, page, statusFilter]);

  const handleSearchChange = (val) => {
    setSearchTerm(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchSalaries(month, 1, statusFilter, val);
    }, 400);
  };

  // ── Staff search for Generate modal ───────────────────────────────────
  const fetchStaffForGenerate = useCallback(async (search) => {
    try {
      const res = await getAllStaff({ n_limit: 50, c_search_term: search });
      if (res.success) setStaffResults(res.staff || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (generateMode && genStep === "select") fetchStaffForGenerate(staffSearch);
  }, [generateMode, genStep, staffSearch]);

  // When genMonth changes, reset attMonth to its default (prev month)
  const genMonthRef = useRef(genMonth);
  useEffect(() => {
    if (genMonthRef.current !== genMonth) {
      setAttMonth(prevMonthOf(genMonth));
      genMonthRef.current = genMonth;
    }
  }, [genMonth]);

  // ── Fetch LOP days from Attendance monthly report ─────────────────────
  // Uses attMonth (user-selectable; defaults to genMonth - 1)
  useEffect(() => {
    if (!generateMode) return;
    setLopLoading(true);
    (async () => {
      try {
        const res = await getMonthlyReport({ month: attMonth });
        if (res.success) {
          const map = {};
          (res.report || []).forEach((r) => {
            map[r._id || r.staffId] = r.lop || 0;
          });
          setLopMap(map);
        } else {
          setLopMap({});
        }
      } catch { setLopMap({}); }
      finally { setLopLoading(false); }
    })();
  }, [generateMode, attMonth]);

  // ── Fetch staff already PAID for genMonth — these are locked, can't regenerate ──
  useEffect(() => {
    if (!generateMode) return;
    (async () => {
      try {
        const res = await getAllSalaries({ month: genMonth, status: "PAID", n_limit: 500 });
        if (res.success) {
          setPaidStaffIds(new Set((res.salaries || []).map((s) => s.staffId?.toString?.() || s.staffId)));
        } else {
          setPaidStaffIds(new Set());
        }
      } catch { setPaidStaffIds(new Set()); }
    })();
  }, [generateMode, genMonth]);

  // ── Toggle staff selection in generate modal ──────────────────────────
  const toggleSelectStaff = (s) => {
    if (paidStaffIds.has(s._id)) return; // already PAID for this month — locked
    setSelectedStaff((prev) => {
      const exists = prev.find((p) => p._id === s._id);
      if (exists) return prev.filter((p) => p._id !== s._id);
      return [...prev, { _id: s._id, name: s.name, role: s.role, department: s.department, baseSalary: s.baseSalary || 0, lopDays: lopMap[s._id] || 0 }];
    });
  };

  // ── Submit generate ────────────────────────────────────────────────────
  // grossSalary auto-fetched from staff.baseSalary on backend
  // Attendance pulled from previous month automatically
  const handleGenerate = async () => {
    const noSalary = selectedStaff.filter((s) => !s.baseSalary || s.baseSalary <= 0);
    if (noSalary.length) {
      showToast(`${noSalary.map((s) => s.name).join(", ")} — baseSalary not set. Update staff first.`, "error");
      return;
    }

    setSubmitting(true);
    try {
      if (selectedStaff.length === 1) {
        const s = selectedStaff[0];
        const res = await generateSalary({ staffId: s._id, month: genMonth, attendanceMonth: attMonth, overrides: { lopDays: s.lopDays || 0 } });
        if (res.success) {
          showToast(res.message || "Salary generated");
          closeGenerate();
          setMonth(genMonth);
          setPage(1);
          fetchSalaries(genMonth, 1, statusFilter, searchTerm);
        } else {
          showToast(res.error || "Failed", "error");
        }
      } else {
        const staffIds = selectedStaff.map((s) => s._id);
        const res = await bulkGenerateSalary({ month: genMonth, attendanceMonth: attMonth, staffIds });
        if (res.success) {
          showToast(`Generated for ${res.generated} staff${res.failed ? `, ${res.failed} failed` : ""}${res.skippedPaid ? `, ${res.skippedPaid} skipped (already PAID)` : ""}`);
          closeGenerate();
          setMonth(genMonth);
          setPage(1);
          fetchSalaries(genMonth, 1, statusFilter, searchTerm);
        } else {
          showToast(res.error || "Failed", "error");
        }
      }
    } catch { showToast("Failed to generate", "error"); }
    finally { setSubmitting(false); }
  };

  const closeGenerate = () => {
    setGenerateMode(false);
    setGenStep("select");
    setGenMonth(currentMonthIST());
    setAttMonth(prevMonthOf(currentMonthIST()));
    setSelectedStaff([]);
    setStaffSearch("");
    setLopMap({});
    setPaidStaffIds(new Set());
  };

  // ── Open detail modal ───────────────────────────────────────────────
  const openDetail = async (id) => {
    setLoading(true);
    try {
      const res = await getSalaryById(id);
      if (res.success) {
        setDetailSalary(res.salary);
        setEditForm({
          bonus:            res.salary.bonus || 0,
          bonusNote:        res.salary.bonusNote || "",
          remarks:          res.salary.remarks || "",
          tds:              res.salary.structure?.tds || 0,
          otherDeductions:  res.salary.structure?.otherDeductions || 0,
          otherAllowances:  res.salary.structure?.otherAllowances || 0,
          professionalTax:  res.salary.structure?.professionalTax || 0,
        });
        setEditMode(false);
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  // ── Save edits ──────────────────────────────────────────────────────
  const handleSaveEdit = async () => {
    setSubmitting(true);
    try {
      const res = await updateSalary(detailSalary._id, {
        bonus:     parseFloat(editForm.bonus) || 0,
        bonusNote: editForm.bonusNote,
        remarks:   editForm.remarks,
        overrides: {
          tds:              parseFloat(editForm.tds) || 0,
          otherDeductions:  parseFloat(editForm.otherDeductions) || 0,
          otherAllowances:  parseFloat(editForm.otherAllowances) || 0,
          professionalTax:  parseFloat(editForm.professionalTax) || 0,
        },
      });
      if (res.success) {
        showToast("Salary updated");
        setDetailSalary(res.salary);
        setEditMode(false);
        fetchSalaries(month, page, statusFilter, searchTerm);
      } else {
        showToast(res.error || "Failed", "error");
      }
    } catch { showToast("Failed", "error"); }
    finally { setSubmitting(false); }
  };

  // ── Approve ─────────────────────────────────────────────────────────
  const handleApprove = async (id) => {
    setSubmitting(true);
    try {
      const res = await approveSalary(id);
      if (res.success) {
        showToast("Salary approved");
        if (detailSalary?._id === id) setDetailSalary(res.salary);
        fetchSalaries(month, page, statusFilter, searchTerm);
      } else showToast(res.error || "Failed", "error");
    } catch { showToast("Failed", "error"); }
    finally { setSubmitting(false); }
  };

  // ── Bulk approve ─────────────────────────────────────────────────────
  const handleBulkApprove = async () => {
    if (!selectedIds.length) return;
    setSubmitting(true);
    try {
      const res = await bulkApproveSalary({ ids: selectedIds });
      if (res.success) {
        showToast(`${res.modifiedCount} salaries approved`);
        setSelectedIds([]);
        fetchSalaries(month, page, statusFilter, searchTerm);
      } else showToast(res.error || "Failed", "error");
    } catch { showToast("Failed", "error"); }
    finally { setSubmitting(false); }
  };

  // ── Mark paid (single or bulk) ─────────────────────────────────────────
  const handleMarkPaid = async () => {
    setSubmitting(true);
    try {
      let res;
      if (payTarget.ids) {
        res = await bulkMarkPaid({ ids: payTarget.ids, ...payForm });
      } else {
        res = await markSalaryPaid(payTarget.id, payForm);
      }
      if (res.success) {
        showToast(payTarget.ids ? `${res.modifiedCount} salaries marked paid` : "Salary marked as paid");
        setPayTarget(null);
        setSelectedIds([]);
        if (detailSalary?._id === payTarget.id) setDetailSalary(res.salary);
        fetchSalaries(month, page, statusFilter, searchTerm);
      } else showToast(res.error || "Failed", "error");
    } catch { showToast("Failed", "error"); }
    finally { setSubmitting(false); }
  };

  // ── Toggle hold ──────────────────────────────────────────────────────
  const handleToggleHold = async (id) => {
    setSubmitting(true);
    try {
      const res = await toggleHold(id, {});
      if (res.success) {
        showToast(res.message);
        if (detailSalary?._id === id) setDetailSalary(res.salary);
        fetchSalaries(month, page, statusFilter, searchTerm);
      } else showToast(res.error || "Failed", "error");
    } catch { showToast("Failed", "error"); }
    finally { setSubmitting(false); }
  };

  // ── Delete ──────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setSubmitting(true);
    try {
      const res = await deleteSalary(confirmDelete);
      if (res.success) {
        showToast("Salary record deleted");
        setConfirmDelete(null);
        setDetailSalary(null);
        fetchSalaries(month, page, statusFilter, searchTerm);
      } else showToast(res.error || "Failed", "error");
    } catch { showToast("Failed", "error"); }
    finally { setSubmitting(false); }
  };

  // ── Download slip ─────────────────────────────────────────────────────
  const handleDownloadSlip = async (salary) => {
    setDownloadingId(salary._id);
    try {
      const filename = `Salary-Slip-${salary.staffName?.replace(/\s+/g, "_")}-${displayMonth(salary).replace(/\s+/g, "_")}.pdf`;
      const res = await downloadSalarySlip(salary._id, filename);
      if (!res.success) showToast(res.error || "Download failed", "error");
    } catch { showToast("Download failed", "error"); }
    finally { setDownloadingId(null); }
  };

  // ── Email slip (single or bulk) ──────────────────────────────────────
  const handleEmailSlip = async () => {
    setEmailing(true);
    try {
      if (emailModal.bulk) {
        const res = await bulkEmailSlips({ ids: selectedIds });
        if (res.success) {
          showToast(`${res.sent} slips emailed${res.failed?.length ? `, ${res.failed.length} failed` : ""}`);
          setSelectedIds([]);
          setEmailModal(null);
        } else showToast(res.error || "Failed", "error");
      } else {
        const res = await emailSalarySlip(emailModal._id, emailForm.toEmail ? { toEmail: emailForm.toEmail } : {});
        if (res.success) {
          showToast(res.message || "Slip emailed");
          setEmailModal(null);
          setEmailForm({ toEmail: "" });
        } else showToast(res.error || "Failed", "error");
      }
    } catch { showToast("Failed to email slip", "error"); }
    finally { setEmailing(false); }
  };

  // ── History modal ────────────────────────────────────────────────────
  const openHistory = async (salary) => {
    setHistoryStaff(salary);
    setHistoryLoading(true);
    try {
      const res = await getStaffSalaryHistory(salary.staffId);
      if (res.success) setHistoryData(res.history || []);
    } catch { /* ignore */ } finally { setHistoryLoading(false); }
  };

  // ── Checkbox toggle ───────────────────────────────────────────────────
  const toggleRowSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const eligible = salaries.filter((s) => s.status !== "PAID").map((s) => s._id);
    if (selectedIds.length === eligible.length) setSelectedIds([]);
    else setSelectedIds(eligible);
  };

  // ── Sub-components ────────────────────────────────────────────────────
  const StatusBadge = ({ status }) => {
    const m = STATUS_META[status] || STATUS_META.DRAFT;
    return (
      <span className="badge" style={{ background: m.bg, color: m.color, fontWeight: 700 }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: m.dot, display: "inline-block", marginRight: 5 }} />
        {m.label}
      </span>
    );
  };

  const SummaryCard = ({ label, value, color, bg, icon }) => (
    <div className="stat-card" style={{ minWidth: 0 }}>
      <div className="stat-icon" style={{ background: bg }}>{icon}</div>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
    </div>
  );

  const selectedEligibleCount = salaries.filter((s) => s.status !== "PAID").length;

  return (
    <div>
      <AccessControl allowedRoles={READ_ROLES} redirectTo="/suggestions" />

      {/* Toast */}
      {toast.show && (
        <div className={`sf-toast sf-toast-${toast.type}`}>
          <span>
            {toast.type === "success" ? <FiCheckCircle size={16} />
              : toast.type === "warning" ? <FiAlertCircle size={16} />
              : <FiXCircle size={16} />}
          </span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* ── Page Header ───────────────────────────────────────────────── */}
      <div className="sf-page-header section">
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: "#1a1a1a", margin: 0 }}>
            Salary Management
          </h2>
          <p style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            Generate, review and process staff salaries
          </p>
        </div>
        {canManage && (
          <button className="btn btn-success" onClick={() => { setGenMonth(month); setGenerateMode(true); }}>
            <FiPlus size={14} /> Generate Salary
          </button>
        )}
      </div>

      {/* ── Summary Stats ─────────────────────────────────────────────── */}
      {totals && (
        <div className="stat-grid section">
          <SummaryCard label="Total Gross"  value={formatCurrency(totals.totalGross)}  color="#0C447C" bg="#E6F1FB" icon={<FiTrendingUp size={18} color="#185FA5" />} />
          <SummaryCard label="Total Net"    value={formatCurrency(totals.totalNet)}    color="#085041" bg="#E1F5EE" icon={<BsCurrencyRupee size={18} color="#0F6E56" />} />
          <SummaryCard label="LOP Deductions" value={formatCurrency(totals.totalLOP)} color="#A32D2D" bg="#FCEBEB" icon={<FiXCircle size={18} color="#A32D2D" />} />
          <SummaryCard label="Total Bonus"  value={formatCurrency(totals.totalBonus)} color="#633806" bg="#FAEEDA" icon={<FiPlus size={18} color="#854F0B" />} />
        </div>
      )}

      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <div className="card section">
        <div className="sf-toolbar">
          <div className="filter-tabs">
            {["all", ...STATUSES].map((s) => (
              <button
                key={s}
                className={`filter-tab ${statusFilter === s ? "active" : ""}`}
                onClick={() => { setStatusFilter(s); setPage(1); }}
              >
                {s === "all" ? <><FiUsers size={12} /> All</> : STATUS_META[s]?.label || s}
                {s !== "all" && totals && (
                  <span style={{ marginLeft: 5, opacity: 0.6 }}>
                    ({s === "DRAFT" ? totals.countDraft : s === "APPROVED" ? totals.countApproved : s === "PAID" ? totals.countPaid : totals.countOnHold})
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="sf-toolbar-right">
            <div className="search-box">
              <FiSearch size={14} className="search-icon" />
              <input placeholder="Search staff…" value={searchTerm} onChange={(e) => handleSearchChange(e.target.value)} />
              {searchTerm && <button className="search-clear" onClick={() => handleSearchChange("")}><FiX size={12} /></button>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <FiCalendar size={14} color="#888" />
              <input
                type="month"
                value={month}
                max={currentMonthIST()}
                style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "6px 10px", fontSize: 13, color: "#333" }}
                onChange={(e) => { setMonth(e.target.value); setPage(1); }}
              />
            </div>
          </div>
        </div>

        {/* Bulk action bar */}
        {selectedIds.length > 0 && canManage && (
          <div style={{ marginTop: 12, padding: "10px 16px", background: "#E6F1FB", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#0C447C" }}>{selectedIds.length} selected</span>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-outline-green" style={{ fontSize: 12 }} onClick={handleBulkApprove} disabled={submitting}>
                <FiCheck size={13} /> Approve Selected
              </button>
              <button className="btn btn-success" style={{ fontSize: 12 }} onClick={() => setPayTarget({ ids: selectedIds })} disabled={submitting}>
                <FiCreditCard size={13} /> Mark Paid
              </button>
              <button className="btn-outline-green" style={{ fontSize: 12 }} onClick={() => setEmailModal({ bulk: true })} disabled={submitting}>
                <FiMail size={13} /> Email Slips
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Salary Table ──────────────────────────────────────────────── */}
      <div className="card section">
        {loading ? (
          <div className="sf-loading" style={{ height: "35vh" }}>
            <div className="spinner" style={{ width: 24, height: 24, borderColor: "#1d9e75", borderTopColor: "transparent" }} />
            <p style={{ marginTop: 12, color: "#888", fontSize: 13 }}>Loading salary records…</p>
          </div>
        ) : salaries.length === 0 ? (
          <div className="sf-empty">
            <BsCurrencyRupee size={40} color="#ddd" />
            <p>No salary records for {month}</p>
            {canManage && <p style={{ fontSize: 12, color: "#bbb" }}>Click "Generate Salary" to create records</p>}
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="sf-table">
              <thead>
                <tr>
                  {canManage && (
                    <th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.length > 0 && selectedIds.length === selectedEligibleCount}
                        onChange={toggleSelectAll}
                      />
                    </th>
                  )}
                  <th>Staff</th>
                  <th style={{ textAlign: "right" }}>Gross</th>
                  <th style={{ textAlign: "center" }}>LOP Days</th>
                  <th style={{ textAlign: "right" }}>Deductions</th>
                  <th style={{ textAlign: "right" }}>Bonus</th>
                  <th style={{ textAlign: "right" }}>Net Payable</th>
                  <th>Status</th>
                  <th style={{ textAlign: "center" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {salaries.map((s) => (
                  <tr key={s._id}>
                    {canManage && (
                      <td>
                        {s.status !== "PAID" && (
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(s._id)}
                            onChange={() => toggleRowSelect(s._id)}
                          />
                        )}
                      </td>
                    )}
                    <td>
                      <div className="sf-member-cell">
                        <div className="sf-avatar">{s.staffName?.charAt(0)?.toUpperCase()}</div>
                        <div>
                          <div className="sf-member-name">{s.staffName}</div>
                          <div className="sf-member-email">{s.staffRole} · {s.department}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ textAlign: "right", fontSize: 13, color: "#555" }}>
                      {formatCurrency(s.structure?.grossSalary)}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {s.lopDays > 0
                        ? <span style={{ fontWeight: 700, fontSize: 12, color: "#A32D2D", background: "#FCEBEB", borderRadius: 6, padding: "2px 8px" }}>{s.lopDays}</span>
                        : <span style={{ color: "#ccc", fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ textAlign: "right", fontSize: 13, color: "#A32D2D" }}>
                      -{formatCurrency((s.structure?.totalDeductions || 0) + (s.lopDeduction || 0))}
                    </td>
                    <td style={{ textAlign: "right", fontSize: 13, color: s.bonus > 0 ? "#085041" : "#ccc" }}>
                      {s.bonus > 0 ? `+${formatCurrency(s.bonus)}` : "—"}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 700, fontSize: 14, color: "#1a1a1a" }}>
                      {formatCurrency(s.finalNetSalary)}
                    </td>
                    <td><StatusBadge status={s.status} /></td>
                    <td style={{ textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                        <button
                          className="icon-btn"
                          title="View details"
                          onClick={() => openDetail(s._id)}
                          style={{ background: "#E6F1FB", color: "#0C447C" }}
                        >
                          <FiEye size={14} />
                        </button>
                        <button
                          className="icon-btn"
                          title="Download slip"
                          disabled={downloadingId === s._id}
                          onClick={() => handleDownloadSlip(s)}
                          style={{ background: "#E1F5EE", color: "#085041" }}
                        >
                          {downloadingId === s._id
                            ? <div className="spinner" style={{ width: 12, height: 12, borderColor: "#085041", borderTopColor: "transparent" }} />
                            : <FiDownload size={14} />}
                        </button>
                        {canManage && (
                          <button
                            className="icon-btn"
                            title="Email slip"
                            onClick={() => setEmailModal(s)}
                            style={{ background: "#FAEEDA", color: "#854F0B" }}
                          >
                            <FiMail size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="sf-pagination">
            <button className="sf-page-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <FiChevronLeft size={14} />
            </button>
            <button className="sf-page-btn" disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}>
              <FiChevronRight size={14} />
            </button>
            <span className="sf-page-info">
              {page} of {pagination.totalPages} · {pagination.totalCount} total
            </span>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          GENERATE MODAL
      ══════════════════════════════════════════════════════════════════ */}
      {generateMode && (
        <div className="modal-overlay" onClick={closeGenerate}>
          <div className="modal-content sf-modal-wide" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <BsCurrencyRupee size={15} style={{ marginRight: 7 }} />
                Generate Salary — {formatMonth(attMonth)} Salary
              </h3>
              <button className="modal-close" onClick={closeGenerate}><FiX size={18} /></button>
            </div>

            <div className="modal-body" style={{ padding: "0 0 4px" }}>
              {/* Month pickers */}
              <div style={{ padding: "14px 24px 0", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <FiCalendar size={14} color="#888" />
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>Salary Month:</label>
                  <input
                    type="month"
                    value={genMonth}
                    max={currentMonthIST()}
                    style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "6px 10px", fontSize: 13, color: "#333" }}
                    onChange={(e) => setGenMonth(e.target.value)}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <FiClock size={14} color="#888" />
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>Attendance Month:</label>
                  <input
                    type="month"
                    value={attMonth}
                    max={currentMonthIST()}
                    style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "6px 10px", fontSize: 13, color: "#333" }}
                    onChange={(e) => setAttMonth(e.target.value)}
                  />
                  {attMonth !== prevMonthOf(genMonth) && (
                    <button
                      style={{ fontSize: 11, color: "#888", background: "none", border: "1px solid #ddd", borderRadius: 6, padding: "3px 8px", cursor: "pointer" }}
                      onClick={() => setAttMonth(prevMonthOf(genMonth))}
                    >
                      Reset to default
                    </button>
                  )}
                </div>
              </div>

              {/* Search */}
              <div style={{ padding: "14px 24px 0" }}>
                <div className="search-box">
                  <FiSearch size={14} className="search-icon" />
                  <input placeholder="Search staff to add…" value={staffSearch} onChange={(e) => setStaffSearch(e.target.value)} />
                  {staffSearch && <button className="search-clear" onClick={() => setStaffSearch("")}><FiX size={12} /></button>}
                </div>
              </div>

              {/* Staff list to select */}
              <div style={{ padding: "12px 24px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#888" }}>Select staff:</div>
                  <div style={{ fontSize: 11, color: "#888" }}>
                    {lopLoading
                      ? <><div className="spinner" style={{ width: 10, height: 10, display: "inline-block", marginRight: 5, verticalAlign: -1 }} /> Checking LOP from {attMonth} attendance…</>
                      : <>LOP shown is from <strong>{attMonth}</strong> attendance</>}
                  </div>
                </div>
                <div style={{ maxHeight: 180, overflowY: "auto", border: "1.5px solid #f3f4f6", borderRadius: 10 }}>
                  {staffResults.length === 0 ? (
                    <div className="sf-empty" style={{ padding: "20px 0" }}>
                      <p style={{ fontSize: 13 }}>No staff found</p>
                    </div>
                  ) : (
                    staffResults.map((s) => {
                      const isSelected = selectedStaff.find((p) => p._id === s._id);
                      const lopDays = lopMap[s._id] || 0;
                      const isPaid = paidStaffIds.has(s._id);
                      return (
                        <div
                          key={s._id}
                          onClick={() => toggleSelectStaff(s)}
                          style={{
                            display: "flex", alignItems: "center", gap: 10, padding: "8px 14px",
                            cursor: isPaid ? "not-allowed" : "pointer",
                            borderBottom: "1px solid #f9fafb",
                            background: isPaid ? "#FAFAFA" : isSelected ? "#E1F5EE" : "#fff",
                            opacity: isPaid ? 0.6 : 1,
                          }}
                        >
                          <input type="checkbox" checked={!!isSelected} disabled={isPaid} onChange={() => {}} />
                          <div className="sf-avatar" style={{ width: 28, height: 28, fontSize: 11 }}>{s.name?.charAt(0)}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>{s.name}</div>
                            <div style={{ fontSize: 11, color: "#888" }}>{s.role} · {s.department}</div>
                          </div>
                          {isPaid ? (
                            <span style={{ fontSize: 11, fontWeight: 600, color: "#085041", background: "#E1F5EE", padding: "2px 8px", borderRadius: 6 }}>
                              <FiCheckCircle size={11} style={{ verticalAlign: -1, marginRight: 3 }} /> Already Paid
                            </span>
                          ) : (
                            <>
                              {lopDays > 0 && (
                                <span style={{ fontSize: 11, fontWeight: 600, color: "#A32D2D", background: "#FCEBEB", padding: "2px 8px", borderRadius: 6 }}>
                                  LOP: {lopDays}d
                                </span>
                              )}
                              {s.baseSalary > 0
                                ? <span style={{ fontSize: 12, fontWeight: 600, color: "#085041", background: "#E1F5EE", padding: "2px 8px", borderRadius: 6 }}>₹{Number(s.baseSalary).toLocaleString("en-IN")}</span>
                                : <span style={{ fontSize: 11, color: "#e53e3e", background: "#FCEBEB", padding: "2px 8px", borderRadius: 6 }}>No salary set</span>
                              }
                            </>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Selected staff summary + info */}
              {selectedStaff.length > 0 && (
                <div style={{ padding: "0 24px 12px" }}>
                  <div style={{ marginTop: 4, padding: "12px 14px", background: "#E1F5EE", borderRadius: 8, fontSize: 12, color: "#085041", lineHeight: 1.6 }}>
                    <FiAlertCircle size={13} style={{ marginRight: 6, verticalAlign: -2 }} />
                    <strong>{selectedStaff.length} staff selected.</strong> <strong>{formatMonth(attMonth)}</strong> salary will be generated using each staff's saved base salary. LOP deductions are from <strong>{attMonth}</strong> attendance.
                  </div>
                  {selectedStaff.some((s) => s.lopDays > 0) && (
                    <div style={{ marginTop: 8, padding: "10px 14px", background: "#FCEBEB", borderRadius: 8, fontSize: 12, color: "#A32D2D", lineHeight: 1.6 }}>
                      <FiAlertCircle size={13} style={{ marginRight: 6, verticalAlign: -2 }} />
                      <strong>LOP applied:</strong>{" "}
                      {selectedStaff.filter((s) => s.lopDays > 0).map((s) => `${s.name} (${s.lopDays}d)`).join(", ")}
                    </div>
                  )}
                  {selectedStaff.some((s) => !s.baseSalary || s.baseSalary <= 0) && (
                    <div style={{ marginTop: 8, padding: "10px 14px", background: "#FCEBEB", borderRadius: 8, fontSize: 12, color: "#A32D2D" }}>
                      <FiAlertCircle size={13} style={{ marginRight: 6, verticalAlign: -2 }} />
                      <strong>Warning:</strong> {selectedStaff.filter((s) => !s.baseSalary || s.baseSalary <= 0).map((s) => s.name).join(", ")} — base salary not set. Go to Staff page and set it first.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <span style={{ fontSize: 12, color: "#888" }}>{selectedStaff.length} staff selected</span>
              <div style={{ display: "flex", gap: 10 }}>
                <button className="btn-outline-green" onClick={closeGenerate}>Cancel</button>
                <button className="btn btn-success" disabled={submitting || selectedStaff.length === 0} onClick={handleGenerate}>
                  {submitting ? (
                    <><div className="spinner" style={{ width: 14, height: 14 }} /> Generating…</>
                  ) : (
                    <><FiSave size={14} /> Generate Salary</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          DETAIL / EDIT MODAL
      ══════════════════════════════════════════════════════════════════ */}
      {detailSalary && (
        <div className="modal-overlay" onClick={() => { setDetailSalary(null); setEditMode(false); }}>
          <div className="modal-content sf-modal-wide" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <FiFileText size={15} style={{ marginRight: 7 }} />
                Salary Slip — {formatMonth(displayMonth(detailSalary))}
              </h3>
              <button className="modal-close" onClick={() => { setDetailSalary(null); setEditMode(false); }}><FiX size={18} /></button>
            </div>

            <div className="modal-body">
              {/* Staff header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                <div className="sf-member-cell">
                  <div className="sf-avatar" style={{ width: 40, height: 40 }}>{detailSalary.staffName?.charAt(0)?.toUpperCase()}</div>
                  <div>
                    <div className="sf-member-name" style={{ fontSize: 14 }}>{detailSalary.staffName}</div>
                    <div className="sf-member-email">{detailSalary.staffRole} · {detailSalary.department}</div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <StatusBadge status={detailSalary.status} />
                  <button className="btn-outline-green" style={{ fontSize: 12, padding: "5px 10px" }} onClick={() => openHistory(detailSalary)}>
                    <FiClock size={12} /> History
                  </button>
                  <button
                    className="btn-outline-green"
                    style={{ fontSize: 12, padding: "5px 10px" }}
                    disabled={downloadingId === detailSalary._id}
                    onClick={() => handleDownloadSlip(detailSalary)}
                  >
                    {downloadingId === detailSalary._id
                      ? <div className="spinner" style={{ width: 12, height: 12 }} />
                      : <FiDownload size={12} />} Download
                  </button>
                  {canManage && (
                    <button className="btn-outline-green" style={{ fontSize: 12, padding: "5px 10px" }} onClick={() => setEmailModal(detailSalary)}>
                      <FiMail size={12} /> Email Slip
                    </button>
                  )}
                </div>
              </div>

              {/* Attendance summary */}
              <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
                <div style={{ fontSize: 12, color: "#888" }}>
                  Working days: <strong style={{ color: "#1a1a1a" }}>{detailSalary.workingDays}</strong>
                </div>
                <div style={{ fontSize: 12, color: "#888" }}>
                  Present days: <strong style={{ color: "#085041" }}>{detailSalary.presentDays}</strong>
                </div>
                <div style={{ fontSize: 12, color: "#888" }}>
                  LOP days: <strong style={{ color: detailSalary.lopDays > 0 ? "#A32D2D" : "#1a1a1a" }}>{detailSalary.lopDays}</strong>
                </div>
              </div>

              {/* Earnings & Deductions breakdown */}
              <div className="sf-two-col" style={{ marginBottom: 16 }}>
                {/* Earnings */}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#085041", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.04em" }}>Earnings</div>
                  {[
                    ["Basic", detailSalary.structure?.basicSalary],
                    ["HRA", detailSalary.structure?.hra],
                    ["Travel Allowance", detailSalary.structure?.travelAllowance],
                    ["Medical Allowance", detailSalary.structure?.medicalAllowance],
                    ["Special Allowance", detailSalary.structure?.specialAllowance],
                    ["Other Allowances", detailSalary.structure?.otherAllowances],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", color: "#555" }}>
                      <span>{label}</span><span>{formatCurrency(val)}</span>
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, padding: "8px 0 0", borderTop: "1px solid #f3f4f6", marginTop: 6, color: "#085041" }}>
                    <span>Gross</span><span>{formatCurrency(detailSalary.structure?.grossSalary)}</span>
                  </div>
                </div>

                {/* Deductions */}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#A32D2D", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.04em" }}>Deductions</div>
                  {[
                    ["PF", detailSalary.structure?.pf],
                    ["ESI", detailSalary.structure?.esi],
                    ["TDS", detailSalary.structure?.tds],
                    ["Professional Tax", detailSalary.structure?.professionalTax],
                    ["Other Deductions", detailSalary.structure?.otherDeductions],
                    ["LOP Deduction", detailSalary.lopDeduction],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", color: "#555" }}>
                      <span>{label}</span><span>-{formatCurrency(val)}</span>
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, padding: "8px 0 0", borderTop: "1px solid #f3f4f6", marginTop: 6, color: "#A32D2D" }}>
                    <span>Total</span><span>-{formatCurrency((detailSalary.structure?.totalDeductions || 0) + (detailSalary.lopDeduction || 0))}</span>
                  </div>
                </div>
              </div>

              {/* Bonus + Net */}
              {!editMode ? (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#FAEEDA", borderRadius: 8, fontSize: 13, marginBottom: 10 }}>
                    <span style={{ color: "#633806" }}>Bonus {detailSalary.bonusNote ? `(${detailSalary.bonusNote})` : ""}</span>
                    <span style={{ fontWeight: 700, color: "#633806" }}>+{formatCurrency(detailSalary.bonus)}</span>
                  </div>
                  {detailSalary.remarks && (
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 10, padding: "8px 14px", background: "#F1EFE8", borderRadius: 8 }}>
                      <FiFileText size={12} style={{ marginRight: 6, verticalAlign: -2 }} />
                      {detailSalary.remarks}
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", background: "#E1F5EE", borderRadius: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#085041" }}>Net Payable</span>
                    <span style={{ fontSize: 20, fontWeight: 800, color: "#085041" }}>{formatCurrency(detailSalary.finalNetSalary)}</span>
                  </div>

                  {/* Payment info */}
                  {detailSalary.status === "PAID" && (
                    <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
                      Paid via <strong>{detailSalary.paymentMode}</strong>
                      {detailSalary.transactionRef && ` · Ref: ${detailSalary.transactionRef}`}
                      {detailSalary.paidOn && ` · ${new Date(detailSalary.paidOn).toLocaleDateString("en-IN")}`}
                    </div>
                  )}

                  {detailSalary.lastEmailedAt && (
                    <div style={{ fontSize: 11, color: "#888", marginTop: 6 }}>
                      <FiSend size={11} style={{ marginRight: 5, verticalAlign: -1 }} />
                      Emailed to {detailSalary.lastEmailedTo} on {new Date(detailSalary.lastEmailedAt).toLocaleString("en-IN")}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Edit form */}
                  <div className="sf-two-col" style={{ marginBottom: 10 }}>
                    <div className="field">
                      <label>Bonus (₹)</label>
                      <input type="number" value={editForm.bonus} onChange={(e) => setEditForm((f) => ({ ...f, bonus: e.target.value }))} />
                    </div>
                    <div className="field">
                      <label>Bonus Note</label>
                      <input value={editForm.bonusNote} onChange={(e) => setEditForm((f) => ({ ...f, bonusNote: e.target.value }))} placeholder="e.g. Diwali bonus" />
                    </div>
                  </div>
                  <div className="sf-two-col" style={{ marginBottom: 10 }}>
                    <div className="field">
                      <label>TDS (₹)</label>
                      <input type="number" value={editForm.tds} onChange={(e) => setEditForm((f) => ({ ...f, tds: e.target.value }))} />
                    </div>
                    <div className="field">
                      <label>Professional Tax (₹)</label>
                      <input type="number" value={editForm.professionalTax} onChange={(e) => setEditForm((f) => ({ ...f, professionalTax: e.target.value }))} />
                    </div>
                  </div>
                  <div className="sf-two-col" style={{ marginBottom: 10 }}>
                    <div className="field">
                      <label>Other Allowances (₹)</label>
                      <input type="number" value={editForm.otherAllowances} onChange={(e) => setEditForm((f) => ({ ...f, otherAllowances: e.target.value }))} />
                    </div>
                    <div className="field">
                      <label>Other Deductions (₹)</label>
                      <input type="number" value={editForm.otherDeductions} onChange={(e) => setEditForm((f) => ({ ...f, otherDeductions: e.target.value }))} />
                    </div>
                  </div>
                  <div className="field" style={{ marginBottom: 10 }}>
                    <label>Remarks</label>
                    <input value={editForm.remarks} onChange={(e) => setEditForm((f) => ({ ...f, remarks: e.target.value }))} placeholder="Optional note" />
                  </div>
                </>
              )}
            </div>

            <div className="modal-actions" style={{ flexWrap: "wrap", gap: 10 }}>
              {canManage && detailSalary.status !== "PAID" && (
                <div style={{ display: "flex", gap: 8 }}>
                  {!editMode ? (
                    <button className="btn-outline-green" style={{ fontSize: 12 }} onClick={() => setEditMode(true)}>
                      <FiEdit2 size={12} /> Edit
                    </button>
                  ) : (
                    <>
                      <button className="btn-outline-green" style={{ fontSize: 12 }} onClick={() => setEditMode(false)}>Cancel</button>
                      <button className="btn btn-success" style={{ fontSize: 12 }} disabled={submitting} onClick={handleSaveEdit}>
                        <FiSave size={12} /> Save
                      </button>
                    </>
                  )}
                  {!editMode && myRole === "SUPERADMIN" && ["DRAFT", "ON_HOLD"].includes(detailSalary.status) && (
                    <button className="icon-btn" style={{ background: "#FCEBEB", color: "#A32D2D" }} onClick={() => setConfirmDelete(detailSalary._id)}>
                      <FiTrash2 size={13} />
                    </button>
                  )}
                </div>
              )}

              {!editMode && canManage && (
                <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                  {detailSalary.status === "DRAFT" && (
                    <button className="btn-outline-green" style={{ fontSize: 12 }} disabled={submitting} onClick={() => handleApprove(detailSalary._id)}>
                      <FiCheck size={12} /> Approve
                    </button>
                  )}
                  {["DRAFT", "ON_HOLD"].includes(detailSalary.status) && (
                    <button
                      className="btn-outline-green"
                      style={{ fontSize: 12, color: detailSalary.status === "ON_HOLD" ? "#085041" : "#A32D2D", borderColor: detailSalary.status === "ON_HOLD" ? "#1d9e75" : "#e53e3e" }}
                      disabled={submitting}
                      onClick={() => handleToggleHold(detailSalary._id)}
                    >
                      {detailSalary.status === "ON_HOLD" ? <><FiPlayCircle size={12} /> Release Hold</> : <><FiPauseCircle size={12} /> Hold</>}
                    </button>
                  )}
                  {detailSalary.status === "APPROVED" && (
                    <button className="btn btn-success" style={{ fontSize: 12 }} disabled={submitting} onClick={() => setPayTarget({ id: detailSalary._id })}>
                      <FiCreditCard size={12} /> Mark Paid
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          PAY MODAL
      ══════════════════════════════════════════════════════════════════ */}
      {payTarget && (
        <div className="modal-overlay" onClick={() => setPayTarget(null)}>
          <div className="modal-content" style={{ maxWidth: 420, height: "auto", minHeight: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FiCreditCard size={15} style={{ marginRight: 7 }} /> Mark as Paid</h3>
              <button className="modal-close" onClick={() => setPayTarget(null)}><FiX size={18} /></button>
            </div>
            <div className="modal-body">
              {payTarget.ids && (
                <div style={{ marginBottom: 14, padding: "8px 14px", background: "#E6F1FB", borderRadius: 8, fontSize: 12, color: "#0C447C" }}>
                  Marking <strong>{payTarget.ids.length}</strong> salary records as paid.
                </div>
              )}
              <div className="field">
                <label>Payment Mode</label>
                <select value={payForm.paymentMode} onChange={(e) => setPayForm((f) => ({ ...f, paymentMode: e.target.value }))}>
                  {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}
                </select>
              </div>
              <div className="field" style={{ marginTop: 14 }}>
                <label>Transaction Reference (optional)</label>
                <input placeholder="e.g. UTR / Cheque No." value={payForm.transactionRef} onChange={(e) => setPayForm((f) => ({ ...f, transactionRef: e.target.value }))} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-outline-green" onClick={() => setPayTarget(null)}>Cancel</button>
              <button className="btn btn-success" disabled={submitting} onClick={handleMarkPaid}>
                {submitting ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Processing…</> : <><FiCheckCircle size={14} /> Confirm Paid</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          HISTORY MODAL
      ══════════════════════════════════════════════════════════════════ */}
      {historyStaff && (
        <div className="modal-overlay" onClick={() => setHistoryStaff(null)}>
          <div className="modal-content" style={{ maxWidth: 540 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FiClock size={15} style={{ marginRight: 7 }} /> Salary History — {historyStaff.staffName}</h3>
              <button className="modal-close" onClick={() => setHistoryStaff(null)}><FiX size={18} /></button>
            </div>
            <div className="modal-body">
              {historyLoading ? (
                <div className="sf-loading" style={{ height: "20vh" }}>
                  <div className="spinner" style={{ width: 22, height: 22, borderColor: "#1d9e75", borderTopColor: "transparent" }} />
                </div>
              ) : historyData.length === 0 ? (
                <div className="sf-empty" style={{ padding: "20px 0" }}>
                  <FiClock size={32} color="#ddd" />
                  <p style={{ fontSize: 13 }}>No history found</p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {historyData.map((h) => (
                    <div key={h._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", border: "1.5px solid #f3f4f6", borderRadius: 10 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>{formatMonth(displayMonth(h))}</div>
                        <div style={{ fontSize: 11, color: "#888" }}>LOP: {h.lopDays} days · Bonus: {formatCurrency(h.bonus)}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#085041" }}>{formatCurrency(h.finalNetSalary)}</div>
                        <StatusBadge status={h.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          EMAIL SLIP MODAL
      ══════════════════════════════════════════════════════════════════ */}
      {emailModal && (
        <div className="modal-overlay" onClick={() => setEmailModal(null)}>
          <div className="modal-content" style={{ maxWidth: 420, height: "auto", minHeight: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FiMail size={15} style={{ marginRight: 7 }} /> {emailModal.bulk ? "Email Salary Slips" : "Email Salary Slip"}</h3>
              <button className="modal-close" onClick={() => setEmailModal(null)}><FiX size={18} /></button>
            </div>
            <div className="modal-body">
              {emailModal.bulk ? (
                <div style={{ padding: "10px 14px", background: "#E6F1FB", borderRadius: 8, fontSize: 13, color: "#0C447C" }}>
                  This will email salary slips to <strong>{selectedIds.length}</strong> staff using their registered email addresses.
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: 12, fontSize: 13, color: "#555" }}>
                    Sending slip for <strong>{emailModal.staffName}</strong> — {formatMonth(displayMonth(emailModal))}
                  </div>
                  <div className="field">
                    <label>Email Address (optional override)</label>
                    <input
                      type="email"
                      placeholder="Leave blank to use staff's registered email"
                      value={emailForm.toEmail}
                      onChange={(e) => setEmailForm({ toEmail: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-outline-green" onClick={() => setEmailModal(null)}>Cancel</button>
              <button className="btn btn-success" disabled={emailing} onClick={handleEmailSlip}>
                {emailing ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Sending…</> : <><FiSend size={14} /> Send Slip</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          CONFIRM DELETE MODAL
      ══════════════════════════════════════════════════════════════════ */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal-content" style={{ maxWidth: 380, height: "auto", minHeight: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FiAlertCircle size={15} style={{ marginRight: 7, color: "#A32D2D" }} /> Delete Salary Record</h3>
              <button className="modal-close" onClick={() => setConfirmDelete(null)}><FiX size={18} /></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: "#555" }}>
                This will permanently delete this salary record. This action cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn-outline-green" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn" style={{ background: "#A32D2D", color: "#fff" }} disabled={submitting} onClick={handleDelete}>
                {submitting ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Deleting…</> : <><FiTrash2 size={14} /> Delete</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}